import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Observable } from 'rxjs';
import { CheckoutObject } from '../../models/checkout';
import {
  PunchoutAuditConfigMetadata,
  PunchoutAuditMetadata,
  PunchoutAuditRecord,
  PunchoutCustomerConfig,
  PunchoutPayload,
  PunchoutProtocol,
  PunchoutSessionContext,
  PunchoutSetupCredentials
} from '../../models/punchout';
import { ActionTypes } from '../../state/auth/actions';

const HOOK_URL_STORAGE_KEY = 'gung_punchout_hookUrl';
const TARGET_STORAGE_KEY = 'gung_punchout_target';
const LAUNCH_PROTOCOL_STORAGE_KEY = 'gung_punchout_launchProtocol';
const BUYER_COOKIE_STORAGE_KEY = 'gung_punchout_buyerCookie';
const SETUP_CREDENTIALS_STORAGE_KEY = 'gung_punchout_setupCredentials';

@Injectable({
  providedIn: 'root'
})
export class PunchoutService {
  protected http = inject(HttpClient);
  protected actions$ = inject(Actions, { optional: true });

  private hookUrl = '';
  private target = '';
  private launchProtocol?: PunchoutProtocol;
  private buyerCookie = '';
  private setupCredentials?: PunchoutSetupCredentials;

  constructor() {
    this.actions$?.pipe(ofType(ActionTypes.Logout)).subscribe(() => this.clearPunchoutParameters());
  }

  setPunchoutParameters(
    hookUrl: string,
    target: string,
    launchProtocol?: string,
    buyerCookie?: string,
    setupCredentials?: PunchoutSetupCredentials
  ): void {
    this.hookUrl = hookUrl || '';
    this.target = target || '';
    this.launchProtocol = this.normalizeProtocol(launchProtocol);
    this.buyerCookie = buyerCookie || '';
    this.setupCredentials = this.normalizeSetupCredentials(setupCredentials);

    this.setOrRemoveStorageItem(HOOK_URL_STORAGE_KEY, this.hookUrl);
    this.setOrRemoveStorageItem(TARGET_STORAGE_KEY, this.target);
    this.setOrRemoveStorageItem(LAUNCH_PROTOCOL_STORAGE_KEY, this.launchProtocol);
    this.setOrRemoveStorageItem(BUYER_COOKIE_STORAGE_KEY, this.buyerCookie);
    this.setOrRemoveStorageItem(
      SETUP_CREDENTIALS_STORAGE_KEY,
      this.setupCredentials ? JSON.stringify(this.setupCredentials) : ''
    );
  }

  getHookUrl(): string {
    if (!this.hookUrl) {
      const stored = sessionStorage.getItem(HOOK_URL_STORAGE_KEY);
      if (stored) {
        this.hookUrl = stored;
      }
    }
    return this.hookUrl;
  }

  getTarget(): string {
    if (!this.target) {
      const stored = sessionStorage.getItem(TARGET_STORAGE_KEY);
      if (stored) {
        this.target = stored;
      }
    }
    return this.target;
  }

  getLaunchProtocol(): PunchoutProtocol {
    if (!this.launchProtocol) {
      this.launchProtocol = this.normalizeProtocol(sessionStorage.getItem(LAUNCH_PROTOCOL_STORAGE_KEY));
    }
    return this.launchProtocol;
  }

  getBuyerCookie(): string {
    if (!this.buyerCookie) {
      const stored = sessionStorage.getItem(BUYER_COOKIE_STORAGE_KEY);
      if (stored) {
        this.buyerCookie = stored;
      }
    }
    return this.buyerCookie;
  }

  getSetupCredentials(): PunchoutSetupCredentials | undefined {
    if (!this.setupCredentials) {
      const stored = sessionStorage.getItem(SETUP_CREDENTIALS_STORAGE_KEY);
      if (stored) {
        try {
          this.setupCredentials = this.normalizeSetupCredentials(JSON.parse(stored));
        } catch {
          this.setupCredentials = undefined;
        }
      }
    }
    return this.setupCredentials;
  }

  getSessionContext(): PunchoutSessionContext {
    return {
      launchProtocol: this.getLaunchProtocol(),
      hookUrl: this.getHookUrl(),
      target: this.getTarget() || '_top',
      buyerCookie: this.getBuyerCookie() || undefined,
      setupCredentials: this.getSetupCredentials()
    };
  }

  clearPunchoutParameters(): void {
    this.hookUrl = '';
    this.target = '';
    this.launchProtocol = undefined;
    this.buyerCookie = '';
    this.setupCredentials = undefined;
    sessionStorage.removeItem(HOOK_URL_STORAGE_KEY);
    sessionStorage.removeItem(TARGET_STORAGE_KEY);
    sessionStorage.removeItem(LAUNCH_PROTOCOL_STORAGE_KEY);
    sessionStorage.removeItem(BUYER_COOKIE_STORAGE_KEY);
    sessionStorage.removeItem(SETUP_CREDENTIALS_STORAGE_KEY);
  }

  persistPunchoutSubmission(
    original: { checkoutObject: CheckoutObject },
    converted: PunchoutPayload,
    audit?: {
      currentConfig?: PunchoutCustomerConfig | null;
      metadata?: PunchoutAuditMetadata;
    }
  ): Observable<void> {
    const session = this.getSessionContext();
    const protocol = audit?.currentConfig?.format ?? this.getProtocolFromPayload(converted);
    const record: PunchoutAuditRecord = {
      protocol,
      launchProtocol: session.launchProtocol,
      hookUrl: session.hookUrl,
      target: session.target,
      buyerCookie: session.buyerCookie,
      setupCredentials: session.setupCredentials,
      currentConfig: this.getAuditConfigMetadata(audit?.currentConfig),
      original,
      converted,
      metadata: {
        payloadKind: converted.kind,
        ...audit?.metadata
      }
    };
    return this.http.post<void>('json/punchout/audit', record);
  }

  persistPunchoutFailure(
    original: { checkoutObject: CheckoutObject } | undefined,
    failureCode: string,
    audit?: {
      currentConfig?: PunchoutCustomerConfig | null;
      metadata?: PunchoutAuditMetadata;
    }
  ): Observable<void> {
    const session = this.getSessionContext();
    const protocol = audit?.currentConfig?.format ?? 'oci';
    const record: PunchoutAuditRecord = {
      protocol,
      launchProtocol: session.launchProtocol,
      hookUrl: session.hookUrl,
      target: session.target,
      buyerCookie: session.buyerCookie,
      setupCredentials: session.setupCredentials,
      currentConfig: this.getAuditConfigMetadata(audit?.currentConfig),
      original,
      failureCode,
      metadata: {
        ...audit?.metadata,
        failureCode
      }
    };
    return this.http.post<void>('json/punchout/audit', record);
  }

  private normalizeProtocol(protocol?: string | null): PunchoutProtocol {
    return protocol?.toLowerCase() === 'cxml' ? 'cxml' : 'oci';
  }

  private normalizeSetupCredentials(input: unknown): PunchoutSetupCredentials | undefined {
    if (!input || typeof input !== 'object') {
      return undefined;
    }
    const source = input as Partial<PunchoutSetupCredentials>;
    const result: PunchoutSetupCredentials = {};
    const keys: Array<keyof PunchoutSetupCredentials> = ['from', 'to', 'sender'];
    for (const key of keys) {
      const entry = source[key];
      if (entry && typeof entry === 'object' && (entry.domain || entry.identity)) {
        result[key] = { domain: entry.domain ?? '', identity: entry.identity ?? '' };
      }
    }
    return result.from || result.to || result.sender ? result : undefined;
  }

  private setOrRemoveStorageItem(key: string, value: string): void {
    if (value) {
      sessionStorage.setItem(key, value);
      return;
    }
    sessionStorage.removeItem(key);
  }

  private getProtocolFromPayload(payload: PunchoutPayload): PunchoutProtocol {
    return payload.kind === 'cxml-form-post' ? 'cxml' : 'oci';
  }

  private getAuditConfigMetadata(
    config?: PunchoutCustomerConfig | null
  ): PunchoutAuditConfigMetadata | undefined {
    if (!config) {
      return undefined;
    }
    return {
      id: config.id,
      name: config.name,
      format: config.format,
      qualifier: config.qualifier,
      redirectUrl: config.redirectUrl
    };
  }
}
